"use client";

import { Button } from "@shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import { Input } from "@shared/components/ui/input";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Switch } from "@shared/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { formatLocalDateTime } from "@shared/lib/formatters";
import { IconCashBanknotePlus, IconDots, IconDownload, IconUpload, IconUserPlus } from "@tabler/icons-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { type AdminOrgRow, type AdminUserRow, useAdminAllOrgs, useAdminAllUsers } from "@/lib/admin/directory.hooks";
import { downloadAdminUsersCsvTemplate } from "@/lib/admin/users.hooks";
import { adminPath } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { AdminEntityDialog } from "./admin.entity-dialog";
import { AdminUsersBulkImportDialog } from "./admin.users.bulk-import-dialog";
import { AdminUsersCreateDialog } from "./admin.users.create-dialog";

// ─── Types ────────────────────────────────────────────────────────────────────

type View = "users" | "orgs";
type DialogEntityType = "user" | "org";

interface AdminEntityDirectoryProps {
  /**
   * @deprecated Capacity allowlist toggles live inside the org dialog Projects tab.
   * Kept optional so older call sites compile; ignored when set.
   */
  showAllowlistToggles?: boolean;
  /** Lock the view to a specific entity type (no toggle shown) */
  fixedView?: View;
}

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function matchesSearch(needle: string, ...haystacks: (string | undefined)[]): boolean {
  if (!needle) return true;
  const q = needle.toLowerCase();
  return haystacks.some((h) => h?.toLowerCase().includes(q));
}

/** Resolve a path UUID that may be an org id or a legacy project id. */
function resolveOrgIdFromPath(pathId: string | null, orgs: AdminOrgRow[]): string | null {
  if (!pathId) return null;
  if (orgs.some((o) => o.id === pathId)) return pathId;
  const owningOrg = orgs.find((o) => o.projects.some((p) => p.id === pathId));
  return owningOrg?.id ?? pathId;
}

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t pt-3">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>
          Next
        </Button>
      </div>
    </div>
  );
};

// ─── Users table ─────────────────────────────────────────────────────────────

interface UsersTableProps {
  rows: AdminUserRow[];
  onSelect: (id: string) => void;
}

const UsersTable = ({ rows, onSelect }: UsersTableProps) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Name</TableHead>
        <TableHead>Email</TableHead>
        <TableHead>Org</TableHead>
        <TableHead>Projects</TableHead>
        <TableHead>Last logged in</TableHead>
        <TableHead>Joined</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.length === 0 && (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
            No users found.
          </TableCell>
        </TableRow>
      )}
      {rows.map((row) => {
        const isInactive = Boolean(row.deleted_at) || row.status === "deactivated" || row.status === "inactive";
        return (
          <TableRow
            key={row.id}
            className={cn("cursor-pointer hover:bg-muted/50", isInactive && "opacity-70")}
            onClick={() => onSelect(row.id)}
          >
            <TableCell className={cn("text-table font-medium", isInactive && "text-destructive")}>{row.name}</TableCell>
            <TableCell className="text-table text-muted-foreground">{row.email}</TableCell>
            <TableCell className="text-table text-muted-foreground">
              {row.org_id ? (
                <Link
                  href={`/orgs/${row.org_id}`}
                  className="underline-offset-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.org_name || row.org_id}
                </Link>
              ) : (
                (row.org_name ?? "—")
              )}
            </TableCell>
            <TableCell className="text-table text-muted-foreground tabular-nums">
              {row.projects.length === 0 ? "—" : row.projects.length}
            </TableCell>
            <TableCell className="text-table text-muted-foreground">
              {row.last_login_at ? formatLocalDateTime(row.last_login_at) : "—"}
            </TableCell>
            <TableCell className="text-table text-muted-foreground">{formatLocalDateTime(row.created_at)}</TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

// ─── Orgs table ───────────────────────────────────────────────────────────────

interface OrgsTableProps {
  rows: AdminOrgRow[];
  onSelect: (id: string) => void;
  onAddCredit: (id: string) => void;
}

const OrgsTable = ({ rows, onSelect, onAddCredit }: OrgsTableProps) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Org</TableHead>
        <TableHead>Owner</TableHead>
        <TableHead>Projects</TableHead>
        <TableHead>Created</TableHead>
        <TableHead />
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.length === 0 && (
        <TableRow>
          <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
            No organizations found.
          </TableCell>
        </TableRow>
      )}
      {rows.map((row) => (
        <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onSelect(row.id)}>
          <TableCell className="text-table font-medium">
            <div className="flex flex-col gap-0.5">
              <span>{row.name}</span>
              {row.slug && <span className="font-mono text-xs text-muted-foreground">{row.slug}</span>}
            </div>
          </TableCell>
          <TableCell className="text-table text-muted-foreground">
            {(() => {
              const email = row.owner_emails[0];
              const userId = row.owner_user_ids[0];
              if (!email) return "—";
              return userId ? (
                <Link
                  href={`/users/${userId}`}
                  className="underline-offset-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {email}
                </Link>
              ) : (
                email
              );
            })()}
          </TableCell>
          <TableCell className="text-table text-muted-foreground tabular-nums">{row.project_count}</TableCell>
          <TableCell className="text-table text-muted-foreground">{formatLocalDateTime(row.created_at)}</TableCell>
          <TableCell className="py-0" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Org actions"
                >
                  <IconDots className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onAddCredit(row.id)}>
                  <IconCashBanknotePlus className="size-4" />
                  Add credits
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// ─── Skeleton loader ──────────────────────────────────────────────────────────

const TableSkeleton = () => (
  <div className="flex flex-col gap-2">
    {["s1", "s2", "s3", "s4", "s5"].map((k) => (
      <Skeleton key={k} className="h-10 w-full" />
    ))}
  </div>
);

// Matches a UUID segment at the end of a pathname, e.g. /orgs/<uuid>
const UUID_PATH_RE = /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// ─── AdminEntityDirectory ─────────────────────────────────────────────────────

export const AdminEntityDirectory = ({ fixedView }: AdminEntityDirectoryProps) => {
  const enabled = true;

  const pathname = usePathname();

  const pathMatch = pathname.match(UUID_PATH_RE);
  const basePath = pathMatch ? pathname.slice(0, -pathMatch[0].length) : pathname;
  const pathEntityId = pathMatch ? pathMatch[1] : null;

  const initialSelection = pathEntityId
    ? {
        id: pathEntityId,
        type: (fixedView === "users" ? "user" : "org") as DialogEntityType,
      }
    : null;

  const [view, setView] = useState<View>(fixedView ?? "users");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelection?.id ?? null);
  const [selectedType, setSelectedType] = useState<DialogEntityType>(initialSelection?.type ?? "user");
  const tabFromUrl =
    typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("tab") ?? undefined) : undefined;
  const [selectedInitialTab, setSelectedInitialTab] = useState<string | undefined>(tabFromUrl);
  const [showDeleted, setShowDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [resolvedPathOrg, setResolvedPathOrg] = useState(false);

  // #FIXME: popstate / replaceState caveats — see prior comments. Kept for deep-link UX.
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(UUID_PATH_RE);
      const id = match ? match[1] : null;
      const tab = id ? (new URLSearchParams(window.location.search).get("tab") ?? undefined) : undefined;
      setSelectedId(id);
      setSelectedInitialTab(tab);
      setSelectedType(fixedView === "users" ? "user" : "org");
      setResolvedPathOrg(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [fixedView]);

  const usersQuery = useAdminAllUsers(showDeleted, enabled && view === "users");
  const orgsQuery = useAdminAllOrgs(enabled && (view === "orgs" || fixedView === "orgs"));

  // Resolve legacy /orgs/<project-uuid> bookmarks to the parent org once data loads.
  useEffect(() => {
    if (fixedView !== "orgs" || !pathEntityId || resolvedPathOrg || orgsQuery.isPending) return;
    const orgs = orgsQuery.data?.adminAllOrgs ?? [];
    if (orgs.length === 0) return;
    const resolved = resolveOrgIdFromPath(pathEntityId, orgs);
    setResolvedPathOrg(true);
    if (resolved && resolved !== selectedId) {
      const tab = selectedInitialTab ? `?tab=${encodeURIComponent(selectedInitialTab)}` : "";
      window.history.replaceState(null, "", `${adminPath(basePath)}/${resolved}${tab}`);
      setSelectedId(resolved);
      setSelectedType("org");
    }
  }, [
    fixedView,
    pathEntityId,
    resolvedPathOrg,
    orgsQuery.isPending,
    orgsQuery.data?.adminAllOrgs,
    selectedId,
    selectedInitialTab,
    basePath,
  ]);

  const openDialog = (id: string, type: DialogEntityType, tab?: string) => {
    const tabSuffix = tab ? `?tab=${encodeURIComponent(tab)}` : "";
    window.history.replaceState(null, "", `${adminPath(basePath)}/${id}${tabSuffix}`);
    setSelectedInitialTab(tab);
    setSelectedId(id);
    setSelectedType(type);
  };

  const closeDialog = () => {
    window.history.replaceState(null, "", adminPath(basePath));
    setSelectedId(null);
    setSelectedInitialTab(undefined);
  };

  const handleViewChange = (v: View) => {
    setView(v);
    setSearch("");
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const allUsers = (usersQuery.data?.adminAllUsers ?? []).filter((u) =>
    matchesSearch(
      search,
      u.name,
      u.email,
      u.org_name,
      u.org_id,
      ...u.projects.map((p) => p.id),
      ...u.projects.map((p) => p.name),
    ),
  );
  const allOrgs = (orgsQuery.data?.adminAllOrgs ?? []).filter((o) =>
    matchesSearch(search, o.name, o.slug ?? undefined, o.id, ...o.owner_emails),
  );

  const isLoading = view === "users" ? usersQuery.isPending : orgsQuery.isPending;
  const isError = view === "users" ? usersQuery.isError : orgsQuery.isError;

  const totalRows = view === "users" ? allUsers.length : allOrgs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const offset = (clampedPage - 1) * PAGE_SIZE;
  const usersPage = allUsers.slice(offset, offset + PAGE_SIZE);
  const orgsPage = allOrgs.slice(offset, offset + PAGE_SIZE);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {!fixedView && (
          <>
            <Button
              variant={view === "users" ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewChange("users")}
            >
              Users
            </Button>
            <Button
              variant={view === "orgs" ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewChange("orgs")}
            >
              Orgs
            </Button>
          </>
        )}
        <Input
          placeholder={
            view === "users" ? "Search by name, email, or org…" : "Search by name, slug, owner email, or UUID…"
          }
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="max-w-sm"
        />
        {!isLoading && (
          <span className="text-sm text-muted-foreground">
            {totalRows} {view}
          </span>
        )}
        {view === "users" && fixedView === "users" && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={showDeleted} onCheckedChange={setShowDeleted} id="show-deleted" />
              <label htmlFor="show-deleted" className="text-muted-foreground">
                Show deleted
              </label>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={downloadAdminUsersCsvTemplate}>
              <IconDownload className="mr-1 size-4" />
              Template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
              <IconUpload className="mr-1 size-4" />
              Import CSV
            </Button>
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <IconUserPlus className="mr-1 size-4" />
              Add user
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-md border">
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : isError ? (
          <p className="py-8 text-center text-sm text-destructive">Failed to load data.</p>
        ) : view === "users" ? (
          <UsersTable rows={usersPage} onSelect={(id) => openDialog(id, "user")} />
        ) : (
          <OrgsTable
            rows={orgsPage}
            onSelect={(id) => openDialog(id, "org")}
            onAddCredit={(id) => openDialog(id, "org", "details")}
          />
        )}
      </div>

      <Pagination page={clampedPage} totalPages={totalPages} onPageChange={setPage} />

      {selectedId && (
        <AdminEntityDialog
          entityType={selectedType}
          entityId={selectedId}
          open={!!selectedId}
          onClose={closeDialog}
          initialTab={selectedInitialTab}
        />
      )}

      <AdminUsersCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <AdminUsersBulkImportDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
};
